import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarPlus, ImagePlus, MessageCircle, Activity as ActivityIcon } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { colors, themeOf } from "@/lib/theme";
import { Avatar, Spinner } from "@/components/peeps/ui";

function ago(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  if (isNaN(then)) return "";
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  const w = Math.floor(d / 7);
  if (w < 5) return `${w}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const META = {
  event: { icon: CalendarPlus, verb: "added a plan" },
  photo: { icon: ImagePlus, verb: "added a photo" },
  comment: { icon: MessageCircle, verb: "commented" },
};

function ActivityRow({ a }) {
  const navigate = useNavigate();
  const meta = META[a.type] || META.event;
  const Icon = meta.icon;
  const th = themeOf(a.group?.theme);
  const isChat = a.from_chat;
  const [showGroups, setShowGroups] = useState(false);
  const groupNames = a.group_names || [];
  const multiGroup = a.type === "event" && a.group_count > 1;
  const first = isChat ? "" : (a.actor?.display_name || "Someone").split(" ")[0];
  const goto = () => {
    if (a.event?.id) navigate(`/event/${a.event.id}`);
    else if (a.group?.id) navigate(`/group/${a.group.id}`);
  };
  return (
    <button
      data-testid={`activity-item-${a.id}`}
      onClick={goto}
      className="flex items-center gap-3 rounded-2xl p-3 w-full text-left transition-colors"
      style={{ background: colors.surfaceSecondary, border: `1px solid ${a.is_new ? th.solid : colors.border}` }}
    >
      <div className="relative shrink-0">
        <Avatar name={isChat ? "Chat" : a.actor?.display_name} uri={isChat ? null : a.actor?.profile_photo_url} size={42} />
        <span className="absolute -bottom-1 -right-1 rounded-full flex items-center justify-center" style={{ width: 20, height: 20, background: th.solid, border: `2px solid ${colors.surfaceSecondary}` }}>
          <Icon size={11} color={th.on} />
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-display text-[13.5px] leading-snug truncate" style={{ color: colors.onSurface }}>
          {isChat ? (
            <>
              <span style={{ color: colors.onSurfaceTertiary }}>{a.source_label || "Added from the group chat"}</span>
              {a.title ? <> <span className="font-bold">{a.title}</span></> : ""}
            </>
          ) : (
            <>
              <span className="font-bold">{first}</span>{" "}
              <span style={{ color: colors.onSurfaceTertiary }}>{meta.verb}</span>
              {a.type === "comment" ? "" : a.title ? <> <span className="font-bold">{a.title}</span></> : ""}
            </>
          )}
        </p>
        {a.type === "comment" && a.title ? (
          <p className="font-display text-[12px] truncate" style={{ color: colors.onSurfaceTertiary }}>&ldquo;{a.title}&rdquo;</p>
        ) : null}
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: th.solid }} />
          {multiGroup ? (
            <span
              data-testid={`activity-groupcount-${a.id}`}
              role="button"
              onClick={(e) => { e.stopPropagation(); setShowGroups((v) => !v); }}
              className="font-display text-[11.5px] underline decoration-dotted"
              style={{ color: colors.faint }}
            >
              In {a.group_count} groups
            </span>
          ) : (
            <span className="font-display text-[11.5px] truncate" style={{ color: colors.faint }}>{a.group?.name || "Group"}</span>
          )}
          <span className="font-display text-[11.5px] shrink-0" style={{ color: colors.faint }}>· {ago(a.created_at)}</span>
        </div>
        {showGroups && groupNames.length > 0 && (
          <div data-testid={`activity-groups-expanded-${a.id}`} className="flex flex-wrap gap-1 mt-1">
            {groupNames.map((n) => (
              <span key={n} className="rounded-full py-0.5 px-2" style={{ background: th.solid + "22" }}>
                <span className="font-display font-medium text-[10px]" style={{ color: th.solid }}>{n}</span>
              </span>
            ))}
          </div>
        )}
      </div>
      {a.is_new && !a.photo_url && (
        <span data-testid={`activity-unread-${a.id}`} className="w-2.5 h-2.5 rounded-full shrink-0" style={{ background: th.solid }} />
      )}
      {a.photo_url && (
        <img src={mediaUrl(a.photo_url)} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />
      )}
    </button>
  );
}

export function ActivityFeed({ showHeading = true }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    api.myActivity().then((r) => {
      if (!alive) return;
      setItems(r || []);
      // Mark as seen shortly after showing, so "new" dots persist for this
      // visit but clear on the next one.
      if ((r || []).some((a) => a.is_new)) {
        setTimeout(() => { api.markActivitySeen().catch(() => {}); }, 2500);
      }
    }).catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="activity-feed" className={showHeading ? "mt-7" : ""}>
      {showHeading && (
        <div className="flex items-center gap-1.5 mb-2.5">
          <ActivityIcon size={15} color={colors.brandSecondary} />
          <p className="font-display font-bold text-[15px]" style={{ color: colors.onSurface }}>Activity</p>
        </div>
      )}
      {items === null ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl p-6 text-center" style={{ background: colors.surfaceSecondary, border: `1px dashed ${colors.border}` }}>
          <p className="font-display text-[13px]" style={{ color: colors.faint }}>No activity yet. New plans, photos and comments from your groups show up here.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((a) => <ActivityRow key={a.id} a={a} />)}
        </div>
      )}
    </div>
  );
}
